import { FullPost } from 'model';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { NavLink } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';
import { useGetFeedQuery } from '../api/FeedApi';
import { useCreatePostMutation, useLazySearchPostQuery } from '../api/PostApi';
import { AuthorHeaderQuestion } from '../components/AuthorHeaderQuestion';
import { SearchBar } from '../components/Forms/SearchBar';
import { Header } from '../components/Header';
import { IconButton } from '../components/IconButton';
import { NoResults } from '../components/NoResults';
import { IconAdd, IconArrowBack, IconHelp } from '../components/QuirkIcons';
import { QuirkIonContent } from '../components/QuirkIonContent';
import { QuirkIonPage } from '../components/QuirkIonPage';
import { QuirkLoader } from '../components/QuirkLoader';
import { useFeedFilters } from '../hooks/useFeedFilters';
import { useQueryWithDebounce } from '../hooks/useQueryWithDebounce';
import { getEmptyPost } from '../util/getEmptyPost';
// Styles
import styles from './CommunityQuestionSearch.module.css';
import { isMutationError } from '../util/error.js';
import { useIonToast } from '@ionic/react';

const FeedView: React.FC<{ item: FullPost }> = ({ item }) => {
	return (
		<NavLink to={`/main/latest/post/show/${item._id}`} className="nounderline">
			<div className={styles.postContainer}>
				<div className={styles.postDetailsContainer}>
					{item.images.length > 0 && (
						<div className={styles.thumbnail}>
							<img src={`/api/post/image/${item._id}/${item.images[0]}`} alt={item.title} />
						</div>
					)}
					<div className={styles.questionContainer}>
						<div>
							<div className={styles.postTitle}>{item?.title}</div>
							<div className={styles.postBody}>{item.contents}</div>
						</div>
						<div className={styles.commentCount}>{item.totalComments}</div>
					</div>
				</div>
				<div className={styles.postHeader}>
					<div className={styles.postName}>
						<AuthorHeaderQuestion authoredItem={item} />
					</div>
				</div>
			</div>
		</NavLink>
	);
};
export const CommunityQuestionSearch = () => {
	const [search, setSearch] = useState('');
	const { data: feed, isLoading: fetchingFeed } = useGetFeedQuery();
	const { fullFeed } = useFeedFilters(
		{
			filterBy: 'communityQuestion',
			topic: ''
		},
		feed
	);
	const { trigger, data: searchData, isFetching, isLoading } = useQueryWithDebounce(useLazySearchPostQuery, 300);
	const [createPost] = useCreatePostMutation();
	const data = search === '' ? fullFeed : searchData;
	const history = useHistory();
	const [toast] = useIonToast();

	const goToCreatePage = async () => {
		const result = await createPost({
			post: getEmptyPost('communityQuestion')
		});
		if (isMutationError(result)) {
			toast({
				color: 'danger',
				message: (result as { error: { data: string; status: number } }).error.data,
				position: 'top',
				duration: 2000
			});
		} else {
			history.push(`/main/latest/communityQuestion/edit/${result.data._id}/${search}`);
		}
	};

	// This handles if the hash sets the search terms
	useEffect(() => {
		if (search === '') {
			const hash = window.location.hash;
			if (hash && hash.length > 1) {
				const searchStr = decodeURI(hash).split('#')[1];
				if (searchStr) {
					trigger({
						search: searchStr,
						postType: 'communityQuestion'
					});
					setSearch(searchStr);
				}
			}
		}
	}, [trigger, search]);

	return (
		<QuirkIonPage name="Ask the Community">
			<Header
				title="Ask the Community"
				left={<IconButton icon={<IconArrowBack />} onClick={() => history.goBack()} />}
			/>

			<QuirkIonContent scroll={false}>
				<>
					<div className={styles.searchContainer}>
						<SearchBar
							placeholder="Search questions"
							value={search}
							isFetching={isFetching}
							isLoading={isLoading}
							onChangeAction={event => {
								history.replace(`/main/communityQuestion/#${event.detail.value}`);
								setSearch(event.detail.value as string);
								trigger({
									search: event.detail.value,
									postType: 'communityQuestion'
								});
							}}
						/>
					</div>
					<div className={styles.addQuestionContainer}>
						<IconButton
							testId="createQuestionButton"
							label="Ask my own question"
							icon={
								<span className="iconCircle">
									<IconAdd />
								</span>
							}
							onClick={goToCreatePage}
						/>
					</div>
					<QuirkLoader
						isLoading={fetchingFeed}
						loadingMessage="Loading community questions..."
						data={data}
						noResults={
							<NoResults
								heading="No questions found."
								subheading="Refine or clear your search filters."
								icon={<IconHelp />}
							/>
						}
					>
						<>
							<div className={styles.feedCaptions}>
								<div>Top Questions</div>
								<div>Answers</div>
							</div>
							<Virtuoso
								totalCount={data?.length}
								data={data as FullPost[]}
								overscan={{ main: 2000, reverse: 2000 }}
								itemContent={(index, item) => (
									<div>
										<FeedView item={item} />
									</div>
								)}
							/>
						</>
					</QuirkLoader>
				</>
			</QuirkIonContent>
		</QuirkIonPage>
	);
};

export default CommunityQuestionSearch;
